import { SYSTEM_ROLE_CODE, writeUser } from "@/schemas/user";
import { useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { useFormUnsavedChangesWarning } from "@/hooks/useFormUnsavedChangesWarning";
import { zodResolver } from "@hookform/resolvers/zod";
import { Module } from "@/constants";
import { fetchSystemRoles } from "@/services/systemRoles";
import useQueryData from "@/hooks/useQueryData";
import { CreateUser, UpdateUser } from "@/types/user";

export const useUserForm = (
  schema: typeof writeUser,
  defaultValues: Partial<CreateUser | UpdateUser>,
) => {
  const form = useForm<CreateUser | UpdateUser>({
    resolver: zodResolver(schema),
    defaultValues,
    disabled: false,
  });

  const systemRolesQuery = useQuery({
    queryKey: [Module.SystemRoles],
    queryFn: () => fetchSystemRoles(),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  useFormUnsavedChangesWarning(form);

  const selectedRoleId = form.watch("system_role_id");
  const registerAsContractor = form.watch("register_as_contractor");

  const systemRoles = useQueryData(systemRolesQuery);

  const isContractorRole = useMemo(
    () =>
      systemRoles.find((role) => role.id === selectedRoleId)?.code ===
      SYSTEM_ROLE_CODE.CONTRACTOR,
    [selectedRoleId, systemRoles],
  );

  const showRegisterAsContractorCheckbox =
    selectedRoleId && systemRolesQuery.isSuccess && !isContractorRole;
  const showTaxStatusSelect = !isContractorRole && registerAsContractor;
  const showPayrollCommentField = !isContractorRole && registerAsContractor;

  const prevIsContractorRole = useRef<boolean>();
  const savedBeforeContractor = useRef<{
    checkbox: boolean;
    taxStatus?: string;
    payrollComment?: string;
  }>();

  useEffect(() => {
    if (prevIsContractorRole.current === undefined) {
      prevIsContractorRole.current = isContractorRole;
      return;
    }

    const changedToContractor =
      !prevIsContractorRole.current && isContractorRole;
    const changedFromContractor =
      prevIsContractorRole.current && !isContractorRole;

    if (changedToContractor) {
      savedBeforeContractor.current = {
        checkbox: form.getValues("register_as_contractor"),
        taxStatus: form.getValues("tax_status"),
        payrollComment: form.getValues("payroll_comment"),
      };
      form.setValue("register_as_contractor", true);
      form.setValue("tax_status", undefined);
      form.setValue("payroll_comment", undefined);
    }

    if (changedFromContractor && savedBeforeContractor.current) {
      form.setValue(
        "register_as_contractor",
        savedBeforeContractor.current.checkbox,
      );
      form.setValue("tax_status", savedBeforeContractor.current.taxStatus);
      form.setValue(
        "payroll_comment",
        savedBeforeContractor.current.payrollComment,
      );
      savedBeforeContractor.current = undefined;
    }

    prevIsContractorRole.current = isContractorRole;
  }, [isContractorRole, form]);

  return {
    form,
    showRegisterAsContractorCheckbox,
    showTaxStatusSelect,
    showPayrollCommentField,
  };
};
